import { Cpu, Gauge, LayoutGrid, List, LogOut, RadioTower, Users } from "lucide-react";

import { cn } from "../../lib/utils";
import type { WallCounts } from "./lib/alerts";
import type { Headroom, Shift, StreamStatus } from "./types";

const STATUS_LABELS: Record<StreamStatus, { text: string; tone: string; dot: string }> = {
  idle: { text: "Standing by", tone: "text-ink-3", dot: "bg-line" },
  connecting: { text: "Connecting…", tone: "text-ink-2", dot: "bg-warning animate-pulse" },
  live: { text: "Live updates", tone: "text-success", dot: "bg-success animate-pulse" },
  polling: { text: "Reconnecting · polling", tone: "text-warning", dot: "bg-warning animate-pulse" },
  error: { text: "Updates unavailable", tone: "text-danger", dot: "bg-danger" },
};

function Count({ label, value, tone, dot }: { label: string; value: number; tone?: string; dot?: string }) {
  return <span className="flex min-w-[68px] items-center gap-2 border-r border-line pr-3 last:border-r-0">
    <span className={cn("h-2 w-2 rounded-full", dot ?? "bg-line")} />
    <span><span className="block text-[8px] font-semibold uppercase tracking-[0.12em] text-ink-3">{label}</span><span className={cn("block font-mono text-base font-semibold leading-4 tabular-nums", tone ?? "text-ink")}>{value}</span></span>
  </span>;
}

export type SupervisionViewMode = "wall" | "queue";

export function StatusStrip({
  counts,
  status,
  view,
  onViewChange,
  declaredCapacity,
  onShift = [],
  headroom = null,
  onEndShift,
  endingShift = false,
  isDemoMode = false,
}: {
  counts: WallCounts;
  status: StreamStatus;
  view: SupervisionViewMode;
  onViewChange: (view: SupervisionViewMode) => void;
  declaredCapacity: number;
  onShift?: Shift[];
  headroom?: Headroom | null;
  onEndShift?: () => void;
  endingShift?: boolean;
  isDemoMode?: boolean;
}) {
  const connection = STATUS_LABELS[status] ?? STATUS_LABELS.idle;
  const liveCalls = counts.live;
  const capacity = headroom?.capacity && headroom.capacity > 0
    ? headroom.capacity
    : Math.max(1, declaredCapacity);
  const load = liveCalls / capacity;
  const loadPct = Math.min(100, Math.round(load * 100));

  const options: { id: SupervisionViewMode; label: string; icon: any }[] = [
    { id: "wall", label: "Wall", icon: LayoutGrid },
    { id: "queue", label: "Queue", icon: List },
  ];

  return <section aria-label="Supervision overview" className="flex flex-wrap items-center gap-2 rounded-xl border border-line bg-elevated px-3 py-1.5 shadow-sm">
    <div className="flex items-center gap-3">
      <Count label="Live calls" value={counts.live} dot={counts.live ? "bg-success" : undefined} />
      <Count label="Critical" value={counts.critical} tone={counts.critical ? "text-danger" : undefined} dot={counts.critical ? "bg-danger" : undefined} />
      <Count label="Warning" value={counts.warning} tone={counts.warning ? "text-warning" : undefined} dot={counts.warning ? "bg-warning" : undefined} />
      <Count label="Overdue" value={counts.overdue} tone={counts.overdue ? "text-danger" : undefined} dot={counts.overdue ? "bg-danger" : undefined} />
    </div>
    <span className="flex min-w-[185px] flex-1 items-center gap-2.5 rounded-lg bg-soft px-3 py-1.5" title={`${liveCalls} live calls across ${capacity} calls of floor capacity`}>
      <Gauge size={14} className="text-ink-3" />
      <span className="flex shrink-0 items-baseline gap-1 text-[10px] font-semibold uppercase tracking-wide text-ink-3">
        Load <strong className="font-mono text-xs text-ink">{liveCalls}</strong>
        <span className="font-mono text-[9px] normal-case tracking-normal">/ {capacity}</span>
      </span>
      <span role="progressbar" aria-label="Live call load" aria-valuemin={0} aria-valuemax={100} aria-valuenow={loadPct} className="h-1.5 min-w-16 flex-1 overflow-hidden rounded-full bg-line"><span className={cn("block h-full transition-[width]", load >= 1 ? "bg-danger" : load >= 0.6 ? "bg-warning" : "bg-success")} style={{ width: `${loadPct}%` }} /></span>
    </span>
    {onShift.length ? <span className="flex items-center gap-2 rounded-lg bg-soft px-3 py-1.5 text-[11px] text-ink-2" title={onShift.map((s) => s.display_name || s.user_id).join(", ")}><Users size={13} className="text-ink-3" /><span className="font-semibold text-ink">{onShift.length}</span> on shift</span> : null}
    <span className={cn("ml-auto flex items-center gap-2 rounded-full border border-line px-3 py-1.5 text-[11px] font-medium", connection.tone)}><span className={cn("h-1.5 w-1.5 rounded-full", connection.dot)} /><RadioTower size={12} /> {connection.text}</span>
    <span className="flex rounded-lg border border-line bg-soft p-0.5">
      {options.map((opt) => {
        const Icon = opt.icon;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onViewChange(opt.id)}
            aria-pressed={view === opt.id}
            className={cn(
              "flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-medium capitalize transition",
              view === opt.id ? "bg-elevated text-ink shadow-sm" : "text-ink-3 hover:text-ink"
            )}
          >
            <Icon size={12} /> {opt.label}
          </button>
        );
      })}
    </span>
    {onEndShift ? <button type="button" onClick={onEndShift} disabled={endingShift} title="Stop supervising and release your share of the calls" className="flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 text-[11px] font-medium text-ink-3 transition hover:text-ink disabled:opacity-50" data-testid="supervision-end-shift"><LogOut size={12} /> {endingShift ? "Ending…" : "End shift"}</button> : null}
  </section>;
}
